import * as THREE from 'three';

import { CoordinateManager } from '../../coordinate-manager';
import { ProjectGeodataManager } from '../../project-geodata-manager';
import { GlobalMercator } from '../../utilities/projections/global-mercator';
import { getCogCorners } from '../cog-asset/cog-projection';

export type OrthoWgs84Bounds = {
  south: number;
  west: number;
  north: number;
  east: number;
};

/**
 * Returns the outer bounding box of the orthotile in scene coordinates (relative to origo).
 * XYZ: the finest level's extent converted to project CRS, then shifted by origo.
 */
export function getOrthotileBounds(projection: string, origo: THREE.Vector3): THREE.Box3 {
  const box = new THREE.Box3();
  const orthoTileGeodata = ProjectGeodataManager.instance().getActivOrthotileGeodata();

  if (orthoTileGeodata?.format === 'COG') {
    const corners = getCogCorners(orthoTileGeodata.source, projection).project;
    return box.setFromPoints(
      corners.map(([x, y]) => new THREE.Vector3(x - origo.x, y - origo.y, 0)),
    );
  }

  // The finest level's tiles fit the image closely; the coarsest level's can be far larger
  const wgs84 = getOrthotileWgs84Bounds();
  if (!wgs84) {
    return box;
  }

  const corners = [
    [wgs84.west, wgs84.south],
    [wgs84.east, wgs84.south],
    [wgs84.east, wgs84.north],
    [wgs84.west, wgs84.north],
  ];
  return box.setFromPoints(
    corners.map(([lon, lat]) => {
      const point = CoordinateManager.getInstance().convertCoords(lon, lat, 'wgs84', projection);
      return new THREE.Vector3(point.x - origo.x, point.y - origo.y, 0);
    }),
  );
}

/**
 * Returns WGS84 lat/lon bounds of the orthotile (max level extent), or undefined if no ortho data.
 */
export function getOrthotileWgs84Bounds(): OrthoWgs84Bounds | undefined {
  const orthoTileGeodata = ProjectGeodataManager.instance().getActivOrthotileGeodata();

  if (orthoTileGeodata?.format === 'COG') {
    const projection = ProjectGeodataManager.instance().getProjectProjection();
    if (!projection) {
      return undefined;
    }
    const corners = getCogCorners(orthoTileGeodata.source, projection).wgs84;
    return {
      west: Math.min(...corners.map(([lon]) => lon)),
      east: Math.max(...corners.map(([lon]) => lon)),
      south: Math.min(...corners.map(([, lat]) => lat)),
      north: Math.max(...corners.map(([, lat]) => lat)),
    };
  }

  const model = orthoTileGeodata?.model;
  if (!model?.levels?.length) {
    return undefined;
  }

  const maxLevel = model.levels[model.levels.length - 1];
  const globalMercator = new GlobalMercator(model.tileSize);
  const highboundsMin = globalMercator.tileLatLonBounds(
    maxLevel.columnMin,
    maxLevel.rowMax,
    maxLevel.zoom,
  );
  const highboundsMax = globalMercator.tileLatLonBounds(
    maxLevel.columnMax,
    maxLevel.rowMin,
    maxLevel.zoom,
  );

  return {
    south: highboundsMin.south,
    west: highboundsMin.west,
    north: highboundsMax.north,
    east: highboundsMax.east,
  };
}
